import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  TrendingUp, 
  Shield, 
  Target, 
  Award, 
  Users, 
  BarChart3,
  CheckCircle2,
  Search
} from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { generateAboutPageSchema, generateOrganizationSchema, generateMultipleSchemas } from '@/lib/schema';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import { BRAND_OPERATOR_LINE } from '@/lib/companyInfo';

const About = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/about/`;

  // Generate structured data
  const aboutPageSchema = generateAboutPageSchema(
    'About Wage Atlas - Professional Salary Research Platform',
    'Wage Atlas provides comprehensive salary data and career insights powered by official U.S. Bureau of Labor Statistics data. Learn about our mission, data sources, and commitment to transparency.',
    pageUrl,
    baseUrl,
    [{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }]
  );

  const organizationSchema = generateOrganizationSchema(baseUrl);
  const structuredData = generateMultipleSchemas([aboutPageSchema, organizationSchema]);

  return (
    <>
      <SeoHead
        title="About Wage Atlas - Professional Salary Research Platform"
        description="Wage Atlas provides comprehensive salary data and career insights powered by official U.S. Bureau of Labor Statistics data. Learn about our mission, data sources, and commitment to transparency."
        canonical={pageUrl}
        schemas={structuredData}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3 space-y-8">
                {/* Header */}
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Users className="h-8 w-8 text-primary" />
                    <h1 className="text-3xl font-bold">About Wage Atlas</h1>
                  </div>
                  <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                    Empowering career decisions with comprehensive, accurate salary data and insights 
                    powered by official U.S. government sources.
                  </p>
                  <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
                    {BRAND_OPERATOR_LINE}
                  </p>
                </div>

                {/* Leaderboard Ad */}
                <AdFrame className="mb-8">
                  <LeaderboardAd />
                </AdFrame>

                {/* Mission Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      Our Mission
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Wage Atlas was founded on a simple principle: everyone deserves access to accurate, 
                      transparent salary information to make informed career decisions. Whether you're 
                      negotiating a raise, exploring new career paths, or researching market rates, 
                      we provide the data you need.
                    </p>
                    <p className="text-muted-foreground">
                      Our platform transforms complex government employment statistics into accessible, 
                      actionable insights. We believe that salary transparency leads to better career 
                      outcomes, fairer compensation, and a more informed workforce.
                    </p>
                  </CardContent>
                </Card>

                {/* Editorial standards */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      How We Work: Editorial Standards
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-muted-foreground">
                    <p>
                      Every data figure on Wage Atlas traces to a named federal source — primarily the
                      BLS Occupational Employment and Wage Statistics survey and BEA Regional Price
                      Parities — and our <Link to="/methodology" className="text-primary hover:underline">methodology page</Link>{' '}
                      documents exactly what we calculate, what we adjust, and what the data cannot show.
                      We state limitations plainly rather than burying them.
                    </p>
                    <p>
                      Our written guides and analysis are produced by the Wage Atlas team and reviewed
                      against the underlying data before publication. We do not publish invented case
                      studies or testimonials, and our editorial content is not influenced by
                      advertisers: ads on this site are clearly distinguishable from content, and no
                      advertiser has input into what we write or which numbers we show.
                    </p>
                    <p>
                      Found an error? We want to know. Email{' '}
                      <a href="mailto:data@wageatlas.com" className="text-primary hover:underline">data@wageatlas.com</a>{' '}
                      and we will investigate and correct confirmed issues, noting material corrections
                      on the affected page.
                    </p>
                  </CardContent>
                </Card>

                {/* Data Sources & Credibility */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      Trusted Data Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary" />
                        Official Government Data
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        All salary data on Wage Atlas comes directly from the U.S. Bureau of Labor 
                        Statistics (BLS), the gold standard for employment and wage statistics. 
                        We use the Occupational Employment and Wage Statistics (OEWS) survey, 
                        which covers over 800 occupations across all U.S. states and metropolitan areas.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">BLS OEWS Data</h4>
                          <p className="text-sm text-muted-foreground">
                            Annual surveys providing mean and median wages, employment levels, 
                            and percentile distributions for hundreds of occupations.
                          </p>
                        </div>
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">BEA Regional Price Parities</h4>
                          <p className="text-sm text-muted-foreground">
                            Cost-of-living adjustments from the Bureau of Economic Analysis 
                            for accurate real wage comparisons across locations.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        For detailed information about our data sources, methodology, and data processing, 
                        see our <Link to="/methodology" className="text-primary hover:underline font-medium">Methodology page</Link> and{' '}
                        <Link to="/data-sources" className="text-primary hover:underline font-medium">Data Sources page</Link>.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* In-Content Ad */}
                <InContentAd />

                {/* Key Features */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5 text-primary" />
                      What Makes Us Different
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium mb-1">Official Government Data</h4>
                          <p className="text-sm text-muted-foreground">
                            Direct from BLS, not estimates or crowdsourced data
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium mb-1">Comprehensive Coverage</h4>
                          <p className="text-sm text-muted-foreground">
                            800+ occupations across all states and metro areas
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium mb-1">Cost-of-Living Adjustments</h4>
                          <p className="text-sm text-muted-foreground">
                            Real wage calculations using BEA Regional Price Parities
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium mb-1">Historical Trends</h4>
                          <p className="text-sm text-muted-foreground">
                            Wage data from 1988 to present for long-term analysis
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium mb-1">Transparent Methodology</h4>
                          <p className="text-sm text-muted-foreground">
                            Full disclosure of data sources and calculation methods
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium mb-1">Export & Analysis Tools</h4>
                          <p className="text-sm text-muted-foreground">
                            CSV, PDF exports and advanced filtering for research
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Use Cases */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Who Uses Wage Atlas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Career Seekers</h4>
                        <p className="text-sm text-muted-foreground">
                          Research salaries for new roles, compare opportunities across locations, 
                          and find high-paying jobs that match your skills.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Professionals</h4>
                        <p className="text-sm text-muted-foreground">
                          Get negotiation-ready salary data, benchmark your compensation, 
                          and make informed career advancement decisions.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Analysts & Researchers</h4>
                        <p className="text-sm text-muted-foreground">
                          Access research-grade data exports, historical trends, and 
                          comprehensive wage analysis tools.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* CTA Section */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Everything Is Free
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Get unlimited access to historical data, advanced analytics, real wage calculations, 
                      and export tools - all completely free, with no accounts or paywalls.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button asChild className="bg-primary hover:bg-primary/90">
                        <Link to="/search">
                          <Search className="mr-2 h-4 w-4" />
                          Explore Salaries
                        </Link>
                      </Button>
                      <Button asChild variant="outline">
                        <Link to="/trends">
                          <TrendingUp className="mr-2 h-4 w-4" />
                          Explore Trends
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Section */}
                <Card>
                  <CardHeader>
                    <CardTitle>Get in Touch</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Have questions about Wage Atlas? We'd love to hear from you.
                    </p>
                    <div className="space-y-2">
                      <p>
                        <strong>General Inquiries:</strong>{' '}
                        <a href="mailto:info@wageatlas.com" className="text-primary hover:underline">
                          info@wageatlas.com
                        </a>
                      </p>
                      <p>
                        <strong>Support:</strong>{' '}
                        <a href="mailto:support@wageatlas.com" className="text-primary hover:underline">
                          support@wageatlas.com
                        </a>
                      </p>
                      <p>
                        <strong>Data Questions:</strong>{' '}
                        <a href="mailto:data@wageatlas.com" className="text-primary hover:underline">
                          data@wageatlas.com
                        </a>
                      </p>
                    </div>
                    <div className="mt-4">
                      <Button asChild variant="outline">
                        <Link to="/contact">
                          Contact Us
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="hidden lg:block space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Links</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Link 
                      to="/methodology" 
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Methodology
                    </Link>
                    <Link 
                      to="/data-sources" 
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Data Sources
                    </Link>
                    <Link 
                      to="/contact" 
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Contact Us
                    </Link>
                    <Link 
                      to="/support" 
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Support
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
